import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../utils/i18n';
import Text from '@adsk/bim360-matrix-react-text';
import Button from '@adsk/bim360-matrix-react-button';
import {
  default as TextInput,
  Behaviors as TextInputBehaviors,
  InputUITypes,
  ValidationTypes
} from '@adsk/bim360-matrix-react-text-input';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import {NotificationTypes, NotificationLevels} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import {issueRootCauseCategoryInputValidators} from '../../../utils/issueRootCauses-helpers';
import IssueRootCauseStatusDropdown from './IssueRootCauseStatusDropdown';
import {presenceValidator} from '../../../utils/validation-helpers';

class IssueRootCauseCategoryAdminCreateModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.getModalDefaults(),
      isOpen: props.isOpen,
      cancelFocused: false
    };

    this.createIssueRootCauseCategory = this.createIssueRootCauseCategory.bind(this);
    this.statusOnChange = this.statusOnChange.bind(this);

    this.createRootCauseCategoryInputProps = {
      behavior: TextInputBehaviors.SINGLE_LINE,
      autofocus: true,
      onChange: value => {
        const valid = this.isIssueRootCauseCategoryNameValid(value);
        this.setState({issueRootCauseCategoryName: {isNew: false, value, valid}});
      },
      className: 'IssueRootCauseCategoryAdminCreateModal__textfield'
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isOpen !== nextProps.isOpen) {
      this.setState({...this.getModalDefaults(), isOpen: nextProps.isOpen});
    }

    if (this.state.isOpen && this.props.isCreating && !nextProps.isCreating) {
      this.closeModal();
    }
  }

  onIssueRootCauseCategoryCreateFinished({title}) {
    this.props.onCreateSuccess();
    this.props.showNotificationAction({
      type: NotificationTypes.TOAST,
      level: NotificationLevels.SUCCESS,
      message: (
        <Text hasHTML>
          {i18n.t('issueRootCauses.notifications.createSuccessIssueRootCauseCategory', {
            issueRootCauseCategoryName: title
          })}
        </Text>
      )
    });
  }

  onIssueRootCauseCategoryCreateFailed({title}) {
    this.props.showNotificationAction({
      type: NotificationTypes.SNACKBAR,
      level: NotificationLevels.ERROR,
      message: (
        <Text hasHTML>
          {i18n.t('issueRootCauses.notifications.createErrorIssueRootCauseCategory', {
            issueRootCauseCategoryName: title
          })}
        </Text>
      )
    });
  }

  createIssueRootCauseCategory() {
    if (this.props.isCreating || !this.state.issueRootCauseCategoryName.valid) {
      return;
    }
    const payload = {
      title: this.state.issueRootCauseCategoryName.value.trim(),
      isActive: this.state.status.value === IssueRootCauseStatusDropdown.IssueRootCausesStatuses.ACTIVE.value
    };
    this.props.createIssueRootCauseCategoryAction(payload, {
      onSuccess: () => this.onIssueRootCauseCategoryCreateFinished(payload),
      onError: () => this.onIssueRootCauseCategoryCreateFailed(payload)
    });
  }

  getModalDefaults() {
    return {
      issueRootCauseCategoryName: {isNew: true, value: '', valid: false},
      status: IssueRootCauseStatusDropdown.IssueRootCausesStatuses.ACTIVE,
      isOpen: false
    };
  }

  isIssueRootCauseCategoryNameValid(value) {
    const validators = issueRootCauseCategoryInputValidators(this.props.issueRootCauseCategoryNames);
    validators.push({
      message: i18n.t('issueRootCauses.modal.required'),
      validate: presenceValidator
    });
    return validators.map(validator => validator.validate(value)).every(isValid => isValid);
  }

  getValidationProps() {
    return {
      type: ValidationTypes.SUBTITLE,
      required: true,
      ignore: this.state.issueRootCauseCategoryName.isNew,
      validator: issueRootCauseCategoryInputValidators(this.props.issueRootCauseCategoryNames),
      typeProps: {
        container: this.props.container
      }
    };
  }

  closeModal() {
    this.props.onModalClose();
    this.setState({isOpen: false});
  }

  handleKeyDown(event) {
    // eslint-disable-next-line default-case
    switch (event.keyCode) {
      case 13: // ENTER
        if (!this.props.isCreating && this.state.issueRootCauseCategoryName.valid && !this.state.cancelFocused) {
          event.preventDefault();
          this.createIssueRootCauseCategory();
        }
        break;
    }
  }

  statusOnChange(status) {
    this.setState({status});
  }

  _renderModalBody() {
    return (
      <div className="IssueRootCauseCategoryAdminCreateModal__body">
        <div className="IssueRootCauseCategoryAdminCreateModal__label">
          <span>{i18n.t('issueRootCauses.modal.categoryName')}</span>
          <span className="IssueRootCauseCategoryAdminCreateModal__required">*</span>
        </div>
        <TextInput
          value={this.state.issueRootCauseCategoryName.value}
          inputUIType={InputUITypes.NORMAL}
          inputProps={this.createRootCauseCategoryInputProps}
          validationProps={this.getValidationProps()}
        />

        <div className="IssueRootCauseCategoryAdminCreateModal__label">
          <span>{i18n.t('issueRootCauses.modal.activeInactive')}</span>
          <span className="IssueRootCauseCategoryAdminCreateModal__required">*</span>
        </div>
        <IssueRootCauseStatusDropdown onChange={this.statusOnChange} selected={this.state.status} />
      </div>
    );
  }

  _renderModalFooter() {
    return (
      <div className="IssueRootCauseCategoryAdminCreateModal__footer">
        <Button
          styleType={Button.StyleType.PRIMARY}
          disabled={this.props.isCreating || !this.state.issueRootCauseCategoryName.valid}
          onClick={this.createIssueRootCauseCategory.bind(this)}
          className="IssueRootCauseCategoryAdminCreateModal__button"
          tabIndex="0"
        >
          {this.props.isCreating ? i18n.t('issueRootCauses.modal.inProgress') : i18n.t('issueRootCauses.modal.create')}
        </Button>
        <Button
          styleType={Button.StyleType.LINK_SECONDARY}
          onClick={this.closeModal.bind(this)}
          className="IssueRootCauseCategoryAdminCreateModal__button IssueRootCauseCategoryAdminCreateModal__button--cancel"
          tabIndex="0"
          onBlur={() => this.setState({cancelFocused: false})}
          onFocus={() => this.setState({cancelFocused: true})}
        >
          {i18n.t('issueRootCauses.modal.cancel')}
        </Button>
      </div>
    );
  }

  render() {
    return (
      <SimpleModal
        container={this.props.container}
        title={i18n.t('issueRootCauses.modal.title')}
        show={this.state.isOpen}
        footer={this._renderModalFooter()}
        onHide={this.closeModal.bind(this)}
        onExited={this.closeModal.bind(this)}
        className="IssueRootCauseCategoryAdminCreateModal"
        modalProps={{
          onKeyDown: this.handleKeyDown.bind(this)
        }}
      >
        {this._renderModalBody()}
      </SimpleModal>
    );
  }
}

IssueRootCauseCategoryAdminCreateModal.displayName = 'IssueRootCauseCategoryAdminCreateModal';

IssueRootCauseCategoryAdminCreateModal.propTypes = {
  container: PropTypes.object.isRequired,
  isCreating: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  createIssueRootCauseCategoryAction: PropTypes.func.isRequired,
  showNotificationAction: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  issueRootCauseCategoryNames: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default IssueRootCauseCategoryAdminCreateModal;
